import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Stack,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { ModalShell } from "../../../shared/ui";
import { isPlanoBloqueado } from "../../../shared/api/planoBloqueado";
import { apiErrorMessage } from "../../../shared/api/apiErrorMessage";
import { hojeISO } from "../../../shared/lib/date";
import { OBSERVACAO_MAX } from "../../plantoes/schemas/plantaoSchema";
import { calendarEventSchema, allowsTime, TITULO_MAX } from "../schemas/calendarEventSchema";
import { EVENT_KIND_META, EVENT_KIND_ORDER } from "../utils/calendarEventKinds";
import { useCreateCalendarEventMutation, useUpdateCalendarEventMutation } from "../hooks/calendarEventQueries";

function emptyValues(data) {
  return { tipo: "folga", titulo: "", dataInicio: data, dataFim: data, horaInicio: "", horas: "1", observacao: "" };
}

function valuesFromEvent(event) {
  return {
    tipo: event.tipo,
    titulo: event.titulo ?? "",
    dataInicio: event.data_inicio,
    dataFim: event.data_fim,
    horaInicio: event.hora_inicio ?? "",
    horas: event.horas != null ? String(event.horas) : "1",
    observacao: event.observacao ?? "",
  };
}

// Novo compromisso (folga, férias, feriado ou evento) ou edição de um existente (`event`).
// `dataInicial` ("yyyy-MM-dd") vem de "Adicionar compromisso" no dia do calendário.
export default function CalendarEventModal({ isOpen, onClose, event = null, dataInicial = null }) {
  const toast = useToast();
  const createMutation = useCreateCalendarEventMutation();
  const updateMutation = useUpdateCalendarEventMutation();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ resolver: zodResolver(calendarEventSchema), defaultValues: emptyValues(hojeISO()) });

  // O modal fica montado entre aberturas: os valores são reaplicados a cada abertura.
  useEffect(() => {
    if (!isOpen) return;
    reset(event ? valuesFromEvent(event) : emptyValues(dataInicial ?? hojeISO()));
  }, [isOpen, event, dataInicial, reset]);

  const tipo = watch("tipo");
  const dataInicio = watch("dataInicio");
  const dataFim = watch("dataFim");

  // Mover o início para depois do fim arrasta o fim junto (o caso comum é um dia só).
  useEffect(() => {
    const fim = getValues("dataFim");
    if (dataInicio && (!fim || fim < dataInicio)) setValue("dataFim", dataInicio);
  }, [dataInicio, getValues, setValue]);

  const showTime = allowsTime({ tipo, dataInicio, dataFim });

  const onSubmit = (values) => {
    const options = {
      onSuccess: () => {
        toast({ title: event ? "Compromisso atualizado" : "Compromisso adicionado", status: "success", duration: 4000 });
        onClose();
      },
      onError: (error) => {
        // 402: quem fala com o médico é o PlanoBloqueadoModal.
        if (isPlanoBloqueado(error)) return;
        toast({
          title: "Não foi possível salvar o compromisso",
          description: apiErrorMessage(error),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    };
    if (event) {
      updateMutation.mutate({ id: event.id, ...values }, options);
    } else {
      createMutation.mutate(values, options);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={event ? "Editar compromisso" : "Novo compromisso"}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={createMutation.isPending || updateMutation.isPending}
    >
      <Stack spacing={4}>
        <FormControl isInvalid={Boolean(errors.tipo)}>
          <FormLabel>Tipo</FormLabel>
          <Select {...register("tipo")}>
            {EVENT_KIND_ORDER.map((kind) => (
              <option key={kind} value={kind}>
                {EVENT_KIND_META[kind].label}
              </option>
            ))}
          </Select>
          <FormHelperText>Compromissos aparecem no calendário e não entram no faturamento.</FormHelperText>
          <FormErrorMessage>{errors.tipo?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.titulo)}>
          <FormLabel>Título</FormLabel>
          <Input maxLength={TITULO_MAX} placeholder={tipo === "evento" ? "Ex.: congresso, dentista" : "Opcional"} {...register("titulo")} />
          <FormErrorMessage>{errors.titulo?.message}</FormErrorMessage>
        </FormControl>

        <HStack spacing={4} align="flex-start">
          <FormControl isInvalid={Boolean(errors.dataInicio)}>
            <FormLabel>De</FormLabel>
            <Input type="date" {...register("dataInicio")} />
            <FormErrorMessage>{errors.dataInicio?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.dataFim)}>
            <FormLabel>Até</FormLabel>
            <Input type="date" min={dataInicio || undefined} {...register("dataFim")} />
            <FormErrorMessage>{errors.dataFim?.message}</FormErrorMessage>
          </FormControl>
        </HStack>

        {showTime ? (
          <HStack spacing={4} align="flex-start">
            <FormControl isInvalid={Boolean(errors.horaInicio)}>
              <FormLabel>Hora de início</FormLabel>
              <Input type="time" {...register("horaInicio")} />
              <FormHelperText>Opcional · sem hora, ocupa o dia inteiro.</FormHelperText>
              <FormErrorMessage>{errors.horaInicio?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={Boolean(errors.horas)}>
              <FormLabel>Duração (horas)</FormLabel>
              <NumberInput min={0.5} max={24} step={0.5}>
                <NumberInputField {...register("horas")} />
              </NumberInput>
              <FormErrorMessage>{errors.horas?.message}</FormErrorMessage>
            </FormControl>
          </HStack>
        ) : null}

        <FormControl isInvalid={Boolean(errors.observacao)}>
          <FormLabel>Observação</FormLabel>
          <Textarea rows={2} maxLength={OBSERVACAO_MAX} {...register("observacao")} />
          <FormErrorMessage>{errors.observacao?.message}</FormErrorMessage>
        </FormControl>
      </Stack>
    </ModalShell>
  );
}
